import { FC, FormEvent } from 'react';
import { Divider, Button, Box, Typography } from '@mui/material';

import { PostStatus, CampaignPPCOptions, CampaignPartnerOptions } from '@libs/types';
import { PostCreateDto } from '@libs/dto';
import { useForm, useDrawer } from '@carcareers/contexts';
import {
  RooftopSelect,
  JobSelect,
  RadioInput,
  DateRangeInput,
  PaymentProfileSelect,
  TextInput,
  CheckboxListInput,
  Select,
  PromoSelect,
} from '@carcareers/components';
import { usePost } from '@carcareers/hooks';

interface PostCreateFormProps {
  onSuccess?: () => void;
}

export const PostCreateForm: FC<PostCreateFormProps> = ({ onSuccess = () => undefined }) => {
  const statusOptions = Object.values([PostStatus.PENDING, PostStatus.ACTIVE]);

  const { setOpen } = useDrawer();
  const { data, setErrors } = useForm<PostCreateDto>();
  const { createPost } = usePost();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const transformedData = {
      ...data,
      budget: Number(data.budget),
      totalPaymentDue: Number(data.totalPaymentDue),
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
    };

    const response = await createPost(transformedData, { onSuccess });

    if (response?.data) {
      setOpen(false);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  return (
    <>
      <Typography variant="h6">Job Posting Details</Typography>
      <RooftopSelect name="job.rooftop" />
      <JobSelect useRooftop name="job" rooftopName="job.rooftop" />
      <Typography variant="h6">Status</Typography>
      <RadioInput options={statusOptions} name="status" />
      <Typography variant="h6">Campaign</Typography>
      <DateRangeInput nameStart="startDate" nameEnd="endDate" />
      <Select name="ppc" label="Price Per Click" options={CampaignPPCOptions} />
      <TextInput name="budget" label="Max Spend" type="number" />
      <CheckboxListInput name="partners" options={CampaignPartnerOptions} />
      <Typography variant="h6">Payment Details</Typography>
      <PromoSelect name="promo" />
      <TextInput name="totalPaymentDue" label="Total Payment Due" type="number" />
      <PaymentProfileSelect name="customerPaymentProfileId" />
      <Divider sx={{ flexGrow: 1 }} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" type="submit" onClick={handleSubmit}>
          Create Post
        </Button>
      </Box>
    </>
  );
};

export default PostCreateForm; 